import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DateRange } from 'react-day-picker';

/**
 * Syncs a DateRange with URL search params (?from=...&to=...).
 * Falls back to the provided default when params are absent.
 */
export function useUrlDateRange(getDefault: () => DateRange): [DateRange, (range: DateRange) => void] {
    const [searchParams, setSearchParams] = useSearchParams();

    const dateRange = useMemo<DateRange>(() => {
        const fromStr = searchParams.get('from');
        const toStr = searchParams.get('to');
        if (!fromStr || !toStr) return getDefault();

        const from = new Date(fromStr);
        const to = new Date(toStr);
        if (isNaN(from.getTime()) || isNaN(to.getTime())) return getDefault();

        return { from, to };
    }, [searchParams, getDefault]);

    const setDateRange = useCallback((range: DateRange) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (range.from) {
                next.set('from', range.from.toISOString());
            } else {
                next.delete('from');
            }
            if (range.to) {
                next.set('to', range.to.toISOString());
            } else {
                next.delete('to');
            }
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    return [dateRange, setDateRange];
}
